import { useMemo } from 'react'
import { useFeatureIsOn } from '@growthbook/growthbook-react'

import { constants } from 'helpers'
import { useAb } from 'hooks'
import localStorage from 'local-storage'

import { normalizeRecommenderStrategies, useCnstrcRecommenderTrackingDataAttrs } from 'modules/constructorIO'
import { useCatalogueId, useCatalogueProducts, useProducts } from 'modules/products'
import { useBestsellers } from 'modules/recommendations'
import { useUser } from 'modules/user'


const LIMIT = 12
const RECOMMENDER_NAME = 'BESTSELLERS'

export const useFirstProducts = () => {
  const isConstructorRecommendationsFeatureEnabled = useFeatureIsOn(constants.features.constructorRecommendations)
  const isConstructorRecommendationsTestEnabled = useAb(constants.abTests.constructorRecommendations) === 'b'

  const areConstructorRecommendationsEnabled = isConstructorRecommendationsFeatureEnabled && isConstructorRecommendationsTestEnabled

  const { gender } = useUser()

  // TODO use picked items or recommendations in future - added on 2021-10-25 by maddoger
  const { catalogueId, isFetching: isCatalogueFetching } = useCatalogueId({
    catalogueName: gender === 'FEMALE' ? 'perfumes' : 'colognes',
    skip: areConstructorRecommendationsEnabled,
  })

  const { data: catalogueData, isFetching: areCatalogueProductsFetching } = useCatalogueProducts({
    variables: {
      id: catalogueId,
      input: {
        filter: [ { type: 'CATEGORY', filterValueIdList: [ 'bestsellers' ] } ],
        offset: 0,
        limit: LIMIT,
        productsFeed: 'BESTSELLERS',
      },
    },
    skip: areConstructorRecommendationsEnabled || !catalogueId,
  })

  const { data: bestsellersData, isFetching: areBestsellersFetching } = useBestsellers({
    gender,
    limit: LIMIT,
    skip: !areConstructorRecommendationsEnabled,
  })

  // we use all saved sources of products
  const postponedItems = localStorage.getItem<QueueModule.AddItemsInput>(constants.localStorageNames.queuePendingProducts)
  const highPriorityProducts = localStorage.getItem<QueueModule.AddItemsInput>(constants.localStorageNames.queueHighPriorityProducts)
  const lowPriorityProducts = localStorage.getItem<QueueModule.AddItemsInput>(constants.localStorageNames.queueLowPriorityProducts)

  const postponedProductsUids = ([] as QueueModule.AddItemsInput).concat(highPriorityProducts, postponedItems, lowPriorityProducts)
    .filter(Boolean)
    .map((item) => item.product.productUid)

  const { products: postponedProducts, isFetching: arePostponedProductsFetching } = useProducts({
    variables: {
      input: {
        slugs: postponedProductsUids,
        pagination: {
          limit: LIMIT,
        },
      },
    },
    skip: !postponedProductsUids?.length,
  })

  const { products: catalogueProducts } = catalogueData || {}
  const { products: bestsellers, pod, productsStrategies, recommenderName } = bestsellersData || {}

  const productList = catalogueProducts || bestsellers

  const isFetching = isCatalogueFetching || areCatalogueProductsFetching || areBestsellersFetching || arePostponedProductsFetching || !productList

  const products = useMemo(() => {
    if (isFetching) {
      return null
    }

    const result: typeof postponedProducts = []
    const ignoreProductsUids = []

    // merge products preferring catalogue ones, because they have more info
    postponedProducts?.forEach((item) => {
      const product = productList.find(({ uid }) => uid === item.uid)

      if (product) {
        ignoreProductsUids.push(item.uid)
        result.push(product)
      }
      else {
        result.push(item)
      }
    })

    productList?.forEach((item) => {
      if (!ignoreProductsUids.includes(item.uid)) {
        result.push(item)
      }
    })

    return result
      .filter((item) => item?.tradingItems?.items?.[0]?.section === 'Subscription')
      .slice(0, 12)
  }, [ isFetching, postponedProducts, productList ])

  const dataAttributes = useCnstrcRecommenderTrackingDataAttrs({
    podId: pod?.id,
    resultId: pod?.resultId,
    resultsCount: products?.length,
  })

  const recommenderStrategies = useMemo(() => {
    return normalizeRecommenderStrategies(productsStrategies)
  }, [ productsStrategies ])

  return {
    dataAttributes: areConstructorRecommendationsEnabled ? dataAttributes : {},
    recommenderName: areConstructorRecommendationsEnabled ? recommenderName : RECOMMENDER_NAME,
    isFetching,
    products,
    recommenderStrategies,
  }
}
